import { getCountriesApi } from "../api/master";
import { CountryItem } from "../models/master";
import { REGIONS } from "../constants/regions";

/**
 * Cache for countries data
 */
let countriesCache: CountryItem[] | null = null;
let regionToCountryIdCache: Record<string, string> | null = null;
let isLoading = false;
let loadPromise: Promise<void> | null = null;

/**
 * Maps region codes to country IDs based on countryShort or countryCode
 */
const buildRegionToCountryIdMapping = (
  countries: CountryItem[]
): Record<string, string> => {
  const mapping: Record<string, string> = {};

  countries.forEach((country) => {
    // Try to match by countryShort (e.g., "UAE", "KSA")
    const countryShort = country.countryShort?.toLowerCase();
    if (countryShort === "uae" || countryShort === "ae") {
      mapping[REGIONS.AE] = country.id;
    } else if (countryShort === "ksa" || countryShort === "sa") {
      mapping[REGIONS.SA] = country.id;
    }

    // Also try to match by countryCode (e.g., "AE", "SA")
    const countryCode = country.countryCode?.toLowerCase();
    if (countryCode === "ae") {
      mapping[REGIONS.AE] = country.id;
    } else if (countryCode === "sa") {
      mapping[REGIONS.SA] = country.id;
    }
  });

  return mapping;
};

/**
 * Fetches countries from API and caches them
 */
export const loadCountries = async (): Promise<void> => {
  // If already loading, return the existing promise
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  // If already loaded, return immediately
  if (countriesCache && regionToCountryIdCache) {
    return Promise.resolve();
  }

  isLoading = true;
  loadPromise = (async () => {
    try {
      const response = await getCountriesApi(true);
      if (response?.data) {
        countriesCache = response.data;
        regionToCountryIdCache = buildRegionToCountryIdMapping(response.data);
      }
    } catch (error) {
      console.error("Error loading countries:", error);
      // Set empty cache on error to prevent infinite retries
      countriesCache = [];
      regionToCountryIdCache = {};
    } finally {
      isLoading = false;
      loadPromise = null;
    }
  })();

  return loadPromise;
};

/**
 * Gets the cached countries
 */
export const getCountries = (): CountryItem[] | null => {
  return countriesCache;
};

/**
 * Gets the country ID for a given region
 */
export const getCountryIdByRegion = (region: string): string | undefined => {
  if (!regionToCountryIdCache) {
    return undefined;
  }
  const normalizedRegion = region.toLowerCase();
  return regionToCountryIdCache[normalizedRegion];
};

/**
 * Resolves region (REGIONS.AE | REGIONS.SA) from a country id.
 * Loads countries if needed. Used after login to sync UI to user's country.
 */
export const getRegionByCountryId = async (
  countryId: string
): Promise<typeof REGIONS.AE | typeof REGIONS.SA | null> => {
  // Important: axios requests are decorated with `X-Region` header based on localStorage `region`.
  // During login we may have a "geo" region stored that differs from the user's actual countryId.
  // If countries are filtered by X-Region, we might not find the provided `countryId`.
  // To make this resolution reliable, temporarily disable X-Region by clearing localStorage.region,
  // clear the cache, and then restore it after resolution.
  const STORAGE_KEY = "region";
  const previousRegion = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;

  try {
    if (typeof window !== "undefined") {
      // Disable X-Region header (interceptor only sets it when `countryId` is truthy).
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ countryName: undefined, countryId: null }));
    }
    clearCountryCache();
    await loadCountries();
  } finally {
    if (typeof window !== "undefined") {
      if (previousRegion !== null) localStorage.setItem(STORAGE_KEY, previousRegion);
      else localStorage.removeItem(STORAGE_KEY);
    }
  }

  const countries = getCountries();
  if (!countries?.length) return null;
  const country = countries.find((c) => c.id === countryId);
  if (!country) return null;
  const short = country.countryShort?.toLowerCase();
  const code = country.countryCode?.toLowerCase();
  if (short === "uae" || short === "ae" || code === "ae") return REGIONS.AE;
  if (short === "ksa" || short === "sa" || code === "sa") return REGIONS.SA;
  return null;
};

/**
 * Clears the cache (useful for testing or refresh)
 */
export const clearCountryCache = (): void => {
  countriesCache = null;
  regionToCountryIdCache = null;
  isLoading = false;
  loadPromise = null;
};




